/**
 * Lightweight JSON diff for the config/alert/override editors.
 * Produces a flat list of "path: before → after" strings suitable for
 * preview in a dialog and for display alongside the audit entry.
 *
 * Intentionally simple: we do not try to reconstruct arrays by identity,
 * we only compare by path. For the config editor this is sufficient
 * because the backing type is entirely object-shaped.
 */

export type DiffLine =
  | { kind: 'add'; path: string; after: unknown }
  | { kind: 'remove'; path: string; before: unknown }
  | { kind: 'change'; path: string; before: unknown; after: unknown };

export function computeDiff(before: unknown, after: unknown, basePath = ''): DiffLine[] {
  if (isEqual(before, after)) return [];

  if (isPlainObject(before) && isPlainObject(after)) {
    const lines: DiffLine[] = [];
    const keys = new Set([...Object.keys(before), ...Object.keys(after)]);
    for (const k of keys) {
      const path = basePath ? `${basePath}.${k}` : k;
      const b = (before as Record<string, unknown>)[k];
      const a = (after as Record<string, unknown>)[k];
      if (b === undefined && a !== undefined) lines.push({ kind: 'add', path, after: a });
      else if (a === undefined && b !== undefined) lines.push({ kind: 'remove', path, before: b });
      else if (!isEqual(b, a)) {
        if (isPlainObject(b) && isPlainObject(a)) {
          lines.push(...computeDiff(b, a, path));
        } else {
          lines.push({ kind: 'change', path, before: b, after: a });
        }
      }
    }
    return lines;
  }

  return [{ kind: 'change', path: basePath || '(root)', before, after }];
}

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function isEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (typeof a !== typeof b) return false;
  if (a === null || b === null) return a === b;
  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return false;
    return a.every((x, i) => isEqual(x, b[i]));
  }
  if (isPlainObject(a) && isPlainObject(b)) {
    const ak = Object.keys(a);
    const bk = Object.keys(b);
    if (ak.length !== bk.length) return false;
    return ak.every((k) => isEqual(a[k], b[k]));
  }
  return false;
}

export function formatDiffValue(v: unknown): string {
  if (v === undefined) return '∅';
  if (v === null) return 'null';
  if (typeof v === 'string') return JSON.stringify(v);
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  try {
    return JSON.stringify(v);
  } catch {
    return String(v);
  }
}
